import {CurriculumItemType} from '@udemy-v2/browse-course';
import {useI18n} from '@udemy-v2/i18n';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useContentLengthFormatter} from '@/features/course-landing-page/hooks/use-content-length-formatter.react-component';
import {CourseAccreditationType} from '@udemy-v2/browse-course';
import {IconName} from '@udemy-v2/icons';
import {IncentiveType} from '@/features/course-landing-page/events/events';
import {useCourseIncludesRolePlay} from '@/features/course-landing-page/components/course-includes/hooks/use-course-includes-role-play';
import {useClpPurchaseContext} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {useCommonData, getUserTypes} from '@udemy-v2/common-data';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {useUserHasOrganization} from '@/lib/hooks/use-user-has-organization';

export enum SupportedIncentives {
    IS_FREE_SEO_EXP,
    VIDEO_CONTENT_LENGTH,
    AUDIO_CONTENT_LENGTH,
    NUM_ARTICLES,
    NUM_QUIZZES,
    NUM_PRACTICE_TESTS,
    NUM_CODING_EXERCISES,
    NUM_ADDITIONAL_RESOURCES,
    HAS_LIFETIME_ACCESS,
    DEVICES_ACCESS,
    HAS_ASSIGNMENTS,
    HAS_CERTIFICATE,
    NUM_CPE_CREDITS,
    PLACEMENT,
    HAS_CLOSED_CAPTIONS,
    HAS_AUDIO_DESCRIPTION,
    ROLE_PLAY,
    GRADED_ROLE_PLAY,
    HAS_IN_COURSE_LABS,
}

/**
 * Interface for course incentives with priority
 */
export interface CourseIncentive<T extends string | boolean | number> {
    value: T;
    priority: number;
    iconGlyph?: IconName;
    text?: string;
    type?: IncentiveType;
    count?: number;
}

/**
 * Priority mapping for each incentive type
 */
export const IncentivePriorities: Record<SupportedIncentives, number> = {
    [SupportedIncentives.ROLE_PLAY]: 0,
    // Same priority as ROLE_PLAY; stable sort keeps the standard Role Play row first
    [SupportedIncentives.GRADED_ROLE_PLAY]: 0,
    [SupportedIncentives.VIDEO_CONTENT_LENGTH]: 1,
    [SupportedIncentives.AUDIO_CONTENT_LENGTH]: 2,
    [SupportedIncentives.NUM_CODING_EXERCISES]: 3,
    [SupportedIncentives.NUM_QUIZZES]: 4,
    [SupportedIncentives.NUM_PRACTICE_TESTS]: 5,
    [SupportedIncentives.HAS_ASSIGNMENTS]: 6,
    [SupportedIncentives.NUM_ARTICLES]: 7,
    [SupportedIncentives.NUM_ADDITIONAL_RESOURCES]: 8,
    [SupportedIncentives.DEVICES_ACCESS]: 9,
    [SupportedIncentives.HAS_LIFETIME_ACCESS]: 10,
    [SupportedIncentives.NUM_CPE_CREDITS]: 11,
    [SupportedIncentives.HAS_CLOSED_CAPTIONS]: 12,
    [SupportedIncentives.HAS_AUDIO_DESCRIPTION]: 13,
    [SupportedIncentives.HAS_CERTIFICATE]: 14,
    [SupportedIncentives.IS_FREE_SEO_EXP]: 15,
    [SupportedIncentives.PLACEMENT]: 16,
    [SupportedIncentives.HAS_IN_COURSE_LABS]: 17,
};

/**
 * Hook that returns the applicable incentives for the current course.
 * Processes raw incentive data into display-ready format.
 * @returns Array of CourseIncentive objects ready for display
 */
export function useCourseIncentives(): CourseIncentive<string | number | boolean>[] {
    const {gettext, ninterpolate, interpolate} = useI18n();
    const formatContentLength = useContentLengthFormatter();
    const course = useClpCourse();
    const {showLifetimeAccess, showLifetimeAccessInBuyBox} = useClpPurchaseContext();
    const hasOrganization = useUserHasOrganization();
    const commonData = useCommonData();
    const {isMXUser} = getUserTypes(commonData);
    const {featureVariantAssignments} = useFeatures(['clp_labs_incentive']);
    const isClpLabsIncentiveEnabled =
        featureVariantAssignments.get('clp_labs_incentive')?.configuration
            ?.enableClpLabsIncentive === true;
    const processedIncentives: CourseIncentive<string | number | boolean>[] = [];

    // Check for free course - don't show if user is UB user
    if (course.isFree && !hasOrganization) {
        processedIncentives.push({
            value: true,
            priority: IncentivePriorities[SupportedIncentives.IS_FREE_SEO_EXP],
            type: IncentiveType.SUBSCRIPTION_DIFFERENTIATION,
            text: gettext('Free course'),
            iconGlyph: 'gift',
        });
    }

    // Handle video content length
    if (course.durationVideoContent && course.durationVideoContent > 0) {
        const videoInMinutes = course.durationVideoContent / 60;
        if (videoInMinutes > 0) {
            const formattedLength = formatContentLength(videoInMinutes);
            processedIncentives.push({
                value: formattedLength,
                priority: IncentivePriorities[SupportedIncentives.VIDEO_CONTENT_LENGTH],
                type: IncentiveType.VIDEO_CONTENT_LENGTH,
                text: interpolate(
                    gettext('%(videoContentLength)s on-demand video'),
                    {videoContentLength: formattedLength},
                    true,
                ),
                iconGlyph: 'video',
            });
        }
    }

    // Handle audio content length
    if (course.durationAudioContent && course.durationAudioContent > 0) {
        const audioInMinutes = course.durationAudioContent / 60;
        if (audioInMinutes > 0) {
            const formattedLength = formatContentLength(audioInMinutes);
            processedIncentives.push({
                value: formattedLength,
                priority: IncentivePriorities[SupportedIncentives.AUDIO_CONTENT_LENGTH],
                type: IncentiveType.AUDIO_CONTENT_LENGTH,
                text: interpolate(
                    gettext('%(audioContentLength)s on-demand audio'),
                    {audioContentLength: formattedLength},
                    true,
                ),
                iconGlyph: 'audio',
            });
        }
    }

    // For curriculum-based incentives, check if curriculum exists
    if (course.curriculum) {
        // Check for articles
        const articleItems = course.curriculum.sections.flatMap((section) =>
            section.items.filter((item) => item.type === CurriculumItemType.ARTICLE_LECTURE),
        );
        if (articleItems.length > 0) {
            const numArticles = articleItems.length;
            processedIncentives.push({
                value: numArticles,
                priority: IncentivePriorities[SupportedIncentives.NUM_ARTICLES],
                type: IncentiveType.NUM_ARTICLES,
                text: ninterpolate('%(count)s article', '%(count)s articles', numArticles, {
                    count: numArticles,
                }),
                count: numArticles,
                iconGlyph: 'article',
            });
        }

        // Check for practice tests
        const practiceTestItems = course.curriculum.sections.flatMap((section) =>
            section.items.filter((item) => item.type === CurriculumItemType.PRACTICE_TEST),
        );
        if (practiceTestItems.length > 0) {
            const numPracticeTests = practiceTestItems.length;
            processedIncentives.push({
                value: numPracticeTests,
                priority: IncentivePriorities[SupportedIncentives.NUM_PRACTICE_TESTS],
                type: IncentiveType.NUM_PRACTICE_TESTS,
                text: ninterpolate(
                    '%(count)s practice test',
                    '%(count)s practice tests',
                    numPracticeTests,
                    {count: numPracticeTests},
                ),
                count: numPracticeTests,
                iconGlyph: 'quiz',
            });
        }

        // Check for coding exercises
        const numCodingExercises = course.curriculum?.contentCounts?.codingExercisesCount || 0;
        if (numCodingExercises > 0) {
            processedIncentives.push({
                value: numCodingExercises,
                priority: IncentivePriorities[SupportedIncentives.NUM_CODING_EXERCISES],
                type: IncentiveType.NUM_CODING_EXERCISES,
                text: ninterpolate(
                    '%(count)s coding exercise',
                    '%(count)s coding exercises',
                    numCodingExercises,
                    {count: numCodingExercises},
                ),
                count: numCodingExercises,
                iconGlyph: 'code',
            });
        }

        // Set the additional resources count
        const numAdditionalResources =
            course.curriculum?.contentCounts?.additionalResourcesCount || 0;
        if (numAdditionalResources > 0) {
            processedIncentives.push({
                value: numAdditionalResources,
                priority: IncentivePriorities[SupportedIncentives.NUM_ADDITIONAL_RESOURCES],
                type: IncentiveType.NUM_RESOURCES,
                text: ninterpolate(
                    '%(count)s downloadable resource',
                    '%(count)s downloadable resources',
                    numAdditionalResources,
                    {count: numAdditionalResources},
                ),
                count: numAdditionalResources,
                iconGlyph: 'downloadable-resource',
            });
        }
    }

    // Set lifetime access based on subscription status
    // If user is a subscriber, they don't have lifetime access
    if (showLifetimeAccess && !showLifetimeAccessInBuyBox) {
        processedIncentives.push({
            value: true,
            priority: IncentivePriorities[SupportedIncentives.HAS_LIFETIME_ACCESS],
            type: IncentiveType.HAS_LIFETIME_ACCESS,
            text: gettext('Full lifetime access'),
            iconGlyph: 'lifetime',
        });
    }

    // Handle devices access
    const isPracticeTestCourse = course.isPracticeTestCourse;

    const deviceText = !isPracticeTestCourse
        ? gettext('Access on mobile and TV')
        : gettext('Access on mobile');

    processedIncentives.push({
        value: deviceText,
        priority: IncentivePriorities[SupportedIncentives.DEVICES_ACCESS],
        type: IncentiveType.DEVICES_ACCESS,
        text: deviceText,
        iconGlyph: 'mobile',
    });

    // Check for assignments - in Python: self.course.practices.exists()
    // We don't have direct access to the practices property
    // This might need to come from a different part of the Course API
    const hasAssignments = course.curriculum?.sections.some((section) => {
        return section.items.some(
            (item) =>
                item.type === CurriculumItemType.PRACTICE_ASSIGNMENT ||
                item.type === CurriculumItemType.PRACTICE_TEST,
        );
    }); // Replace with actual check when data is available
    if (hasAssignments) {
        processedIncentives.push({
            value: true,
            priority: IncentivePriorities[SupportedIncentives.HAS_ASSIGNMENTS],
            type: IncentiveType.HAS_ASSIGNMENTS,
            text: gettext('Assignments'),
            iconGlyph: 'assignment-outline',
        });
    }

    // Certificate is available for paid courses only (not free)
    if (course.hasCertificate && !course.isFree) {
        // In Python, this uses a specific method: self.course.has_certificate_for_enrollment()
        // Here we're approximating based on available data
        processedIncentives.push({
            value: true,
            priority: IncentivePriorities[SupportedIncentives.HAS_CERTIFICATE],
            type: IncentiveType.HAS_CERTIFICATE,
            text: gettext('Certificate of completion'),
            iconGlyph: 'trophy',
        });
    }

    // Set CPE credits
    // In Python, we check if the course is CPE compliant and has CPE credits
    const cpeCredits = course.accreditations
        ? course.accreditations
              .filter((accreditation) => accreditation.type === CourseAccreditationType.CPE)
              .reduce((sum, accreditation) => sum + accreditation.creditCount, 0)
        : 0;

    if (cpeCredits > 0) {
        processedIncentives.push({
            value: cpeCredits,
            priority: IncentivePriorities[SupportedIncentives.NUM_CPE_CREDITS],
            type: IncentiveType.NUM_CPE_CREDITS,
            text: ninterpolate('%(count)s CPE credit', '%(count)s CPE credits', cpeCredits, {
                count: cpeCredits,
            }),
            count: cpeCredits,
            iconGlyph: 'medal',
        });
    }

    // Check for closed captions
    if (course.captions && course.captions.length > 0) {
        processedIncentives.push({
            value: true,
            priority: IncentivePriorities[SupportedIncentives.HAS_CLOSED_CAPTIONS],
            type: IncentiveType.HAS_CLOSED_CAPTIONS,
            text: gettext('Closed captions'),
            iconGlyph: 'captions-outline',
        });
    }

    // Check for audio descriptions
    const hasAudioDescription = course.captions?.some(
        (caption) => caption.locale === 'audio-description',
    );
    if (hasAudioDescription) {
        processedIncentives.push({
            value: true,
            priority: IncentivePriorities[SupportedIncentives.HAS_AUDIO_DESCRIPTION],
            type: IncentiveType.HAS_AUDIO_DESCRIPTION,
            text: gettext('Audio descriptions'),
            iconGlyph: 'ad-audio-outline',
        });
    }

    // Check for in-course labs incentive
    const inCourseLabsCount = course.curriculum?.contentCounts?.inCourseLabsCount ?? 0;
    if (isMXUser && isClpLabsIncentiveEnabled && inCourseLabsCount > 0) {
        processedIncentives.push({
            value: true,
            priority: IncentivePriorities[SupportedIncentives.HAS_IN_COURSE_LABS],
            type: IncentiveType.HAS_IN_COURSE_LABS,
            text: gettext('Optional: hands-on practice for an additional fee'),
            iconGlyph: 'labs',
        });
    }

    // Check for role play
    const {hasRolePlay, numRolePlays, hasGradedRolePlay, numGradedRolePlays} =
        useCourseIncludesRolePlay(course);

    if (hasRolePlay) {
        processedIncentives.push({
            value: numRolePlays,
            priority: IncentivePriorities[SupportedIncentives.ROLE_PLAY],
            type: IncentiveType.ROLE_PLAY,
            text: ninterpolate('%(count)s Role Play', '%(count)s Role Plays', numRolePlays, {
                count: numRolePlays,
            }),
            count: numRolePlays,
            iconGlyph: 'people',
        });
    }

    if (hasGradedRolePlay) {
        processedIncentives.push({
            value: numGradedRolePlays,
            priority: IncentivePriorities[SupportedIncentives.GRADED_ROLE_PLAY],
            type: IncentiveType.GRADED_ROLE_PLAY,
            text: ninterpolate(
                '%(count)s Graded Role Play',
                '%(count)s Graded Role Plays',
                numGradedRolePlays,
                {count: numGradedRolePlays},
            ),
            count: numGradedRolePlays,
            iconGlyph: 'presentation',
        });
    }

    // Sort by priority
    return processedIncentives.sort((a, b) => a.priority - b.priority);
}
